import { Link } from "@tanstack/react-router";
import { Alert, Card, DataTable, type ColumnDef } from "@/deps/ui-library";
import { usePartyStatement } from "@/api/queries";
import type { PartyStanding, PartyStatement } from "@/api/types";
import { bandLabels } from "@/features/reports/AgeingPage";
import { DownloadMenu } from "@/features/reports/DownloadMenu";
import { DateField } from "@/features/reports/reportParts";
import { formatDate } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { paisa } from "./standing";

/** What a customer's page and a supplier's page share: the ageing strip, the ledger and a detail row. */

export function AgeingStrip({ standing }: { standing: PartyStanding }) {
  const bands = bandLabels(standing.bucketDays);
  return (
    <Card size="none" className="overflow-hidden">
      <dl className="grid grid-cols-2 md:grid-cols-5">
        {bands.map((band, index) => {
          const value = standing.buckets[index] ?? "0.00";
          return (
            <div
              key={band}
              className="border-line-soft border-r border-b px-4 py-2.5 last:border-r-0"
            >
              <dt className="text-fg text-xs font-medium">{band}</dt>
              <dd
                className={`num mt-0.5 text-sm font-semibold ${
                  index > 0 && paisa(value) > 0 ? "text-destructive" : "text-fg"
                }`}
              >
                {formatAmount(value)}
              </dd>
            </div>
          );
        })}
      </dl>
    </Card>
  );
}

export const TYPE_LABELS: Record<string, string> = {
  sales_invoice: "Invoice",
  receipt: "Receipt",
  credit_note: "Credit note",
  customer_advance_used: "Advance used",
  supplier_bill: "Bill",
  supplier_payment: "Payment",
  supplier_debit_note: "Debit note",
  supplier_advance_used: "Advance used",
  manual_journal: "Journal voucher",
  fund_transfer: "Fund transfer",
  customer_receipt: "Receipt",
  employee_payment: "Payment",
  employee_receipt: "Receipt",
  opening_balance: "Opening balance",
  opening_balance_adjustment: "Opening balance adjustment",
};

const OPENING = "opening";
const CLOSING = "closing";

type StatementRow = PartyStatement["lines"][number];

function statementRow(
  key: string,
  details: string,
  balance: string,
  debit = "",
  credit = "",
): StatementRow {
  return {
    journalId: key,
    documentId: key,
    date: "",
    documentType: "",
    number: "",
    details,
    debit,
    credit,
    balance,
  };
}

function DocumentLink({ line }: { line: StatementRow }) {
  const params = { id: line.documentId };
  if (line.documentType === "sales_invoice") {
    return (
      <Link to="/sales/invoices/$id" params={params} className="text-primary hover:underline">
        {line.number}
      </Link>
    );
  }
  if (line.documentType === "receipt") {
    return (
      <Link to="/sales/receipts/$id" params={params} className="text-primary hover:underline">
        {line.number}
      </Link>
    );
  }
  if (line.documentType === "credit_note") {
    return (
      <Link to="/sales/credit-notes/$id" params={params} className="text-primary hover:underline">
        {line.number}
      </Link>
    );
  }
  if (line.documentType === "supplier_bill") {
    return (
      <Link to="/purchases/bills/$id" params={params} className="text-primary hover:underline">
        {line.number}
      </Link>
    );
  }
  if (line.documentType === "supplier_payment") {
    return (
      <Link to="/purchases/payments/$id" params={params} className="text-primary hover:underline">
        {line.number}
      </Link>
    );
  }
  if (line.documentType === "supplier_debit_note") {
    return (
      <Link
        to="/purchases/debit-notes/$id"
        params={params}
        className="text-primary hover:underline"
      >
        {line.number}
      </Link>
    );
  }
  return <>{line.number}</>;
}

export function LedgerTab({
  id,
  kind,
  name,
  from,
  to,
  setDate,
}: {
  id: string;
  kind: "customer" | "supplier";
  name: string;
  from: string;
  to: string;
  setDate: (key: "from" | "to", value: string) => void;
}) {
  const { data, isPending, error } = usePartyStatement(id, kind, from, to);
  const period = `${formatDate(from)} to ${formatDate(to)}`;
  const isEdge = (row: StatementRow) => row.journalId === OPENING || row.journalId === CLOSING;

  const figure = (key: "debit" | "credit" | "balance") => ({
    meta: { textAlign: "right" as const, cellClassName: "num" },
    cell: ({ row: { original: row } }: { row: { original: StatementRow } }) => {
      const value = row[key];
      if (value === "" || (key !== "balance" && !isEdge(row) && paisa(value) === 0)) return "";
      const text = formatAmount(value);
      return isEdge(row) ? <span className="font-semibold">{text}</span> : text;
    },
  });

  const columns: Array<ColumnDef<StatementRow>> = [
    {
      id: "date",
      header: "Date",
      cell: ({ row: { original: row } }) => (row.date ? formatDate(row.date) : ""),
    },
    {
      id: "document",
      header: "Document",
      cell: ({ row: { original: row } }) => (row.number ? <DocumentLink line={row} /> : ""),
    },
    {
      id: "type",
      header: "Type",
      cell: ({ row: { original: row } }) =>
        TYPE_LABELS[row.documentType] ?? row.documentType.replaceAll("_", " "),
    },
    {
      id: "details",
      header: "Details",
      cell: ({ row: { original: row } }) =>
        isEdge(row) ? <span className="font-semibold">{row.details}</span> : (row.details ?? ""),
    },
    { id: "debit", header: "Debit", ...figure("debit") },
    { id: "credit", header: "Credit", ...figure("credit") },
    { id: "balance", header: "Balance", ...figure("balance") },
  ];

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div className="flex flex-wrap items-end gap-2">
          <DateField label="From" value={from} onChange={(value) => setDate("from", value)} />
          <DateField label="To" value={to} onChange={(value) => setDate("to", value)} />
        </div>
        <span className="flex items-center gap-2">
          <DownloadMenu
            title={`${name} statement`}
            period={period}
            exportable={
              data
                ? () => ({
                    title: `${name} statement`,
                    period,
                    columns: [
                      { header: "Date" },
                      { header: "Document" },
                      { header: "Type" },
                      { header: "Details" },
                      { header: "Debit", numeric: true },
                      { header: "Credit", numeric: true },
                      { header: "Balance", numeric: true },
                    ],
                    rows: [
                      ["", "", "", "Opening balance", "", "", data.opening],
                      ...data.lines.map((line) => [
                        line.date,
                        line.number,
                        TYPE_LABELS[line.documentType] ?? line.documentType,
                        line.details ?? "",
                        line.debit,
                        line.credit,
                        line.balance,
                      ]),
                    ],
                    totals: [
                      "",
                      "",
                      "",
                      "Closing balance",
                      data.totalDebit,
                      data.totalCredit,
                      data.closing,
                    ],
                  })
                : undefined
            }
          />
        </span>
      </div>

      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(error as Error).message}
        />
      ) : null}

      <Card size="none" className="overflow-hidden">
        <DataTable
          aria-label={kind === "customer" ? "Customer ledger" : "Supplier ledger"}
          size="sm"
          enableSorting={false}
          columns={columns}
          data={
            data
              ? [
                  statementRow(OPENING, "Opening balance", data.opening),
                  ...data.lines,
                  statementRow(
                    CLOSING,
                    "Closing balance",
                    data.closing,
                    data.totalDebit,
                    data.totalCredit,
                  ),
                ]
              : []
          }
          getRowId={(row, index) => `${row.journalId}-${index}`}
          loading={isPending}
          emptyContent="Nothing in this period."
          pagination={{ mode: "manual", show: false }}
        />
      </Card>
      <p className="text-fg-muted text-xs">
        Advances are on this ledger, so the balance is net of money held on account.
        {kind === "customer"
          ? " Outstanding above is invoices only."
          : " Owed above is bills only."}
        {data && data.lines.length < data.allLines
          ? ` Showing the first ${data.lines.length} of ${data.allLines} lines; download or narrow the dates for the rest.`
          : ""}
      </p>
    </div>
  );
}

export function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 py-1 text-sm">
      <dt className="text-fg-muted">{label}</dt>
      <dd className="text-fg text-right">{value || "Not given"}</dd>
    </div>
  );
}
